// useQuizSession — quiz state machine: setup → running → finished.
//
// Extracted from quiz-view.tsx (~220 lines of state + session lifecycle +
// answer handling + wrong-answer persistence + bookmark toggle).  The view
// is now responsible only for JSX rendering + keyboard handler.
//
// Session 14 Step 3 · Tier 2 View Decomposition.

import { useMemo, useState, useCallback } from 'react';
import { updateProgress } from '../../storage/idb.js';
import { shuffle, isCorrectAnswer } from '../quiz-utils.js';
import { recordWrongAnswerEntry } from '../wrong-answer.js';
import { toggleBookmark } from '../bookmark.js';
import { resolveItem } from '../resolve-item.js';
import type { ActiveQuizSession, MCQ, TrueFalse, CourseDraft, CourseProgress } from '@examforge/shared/schemas';
import type { IEventBus } from '../event-bus.js';

// ── Types (exported for view JSX) ───────────────────────────────────────────

export type Phase = 'setup' | 'running' | 'finished';
export type QuizKind = 'mcq' | 'tf';

export type QuizItem =
  | { kind: 'mcq'; data: MCQ }
  | { kind: 'tf'; data: TrueFalse };

export interface SetupConfig {
  caseId: string | 'all';
  count: number | 'all';
}

export interface AttemptRecord {
  itemId: string;
  pickedIndex: number;
  correct: boolean;
}

export const PRESET_COUNTS: readonly (number | 'all')[] = [10, 20, 30, 'all'];

// ── Helpers ─────────────────────────────────────────────────────────────────

export function mkSessionId(): string {
  return `qs_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
}

// ── Hook ────────────────────────────────────────────────────────────────────

export function useQuizSession(
  draft: CourseDraft,
  progress: CourseProgress,
  itemPool: QuizItem[],
  eventBus: IEventBus,
  refreshProgress: () => Promise<void>,
  drillItems: string | null,
  deepLinkQueue: QuizItem[] | null,
  caseParam: string | null,
) {
  // ── Initial phase ───────────────────────────────────────────────────────

  const persistedSession = progress.activeQuizSession;

  const initialPhase: Phase = drillItems || (deepLinkQueue && deepLinkQueue.length > 0)
    ? 'running'
    : persistedSession
      ? 'setup'
      : 'setup';

  const initialConfig: SetupConfig = caseParam
    ? { caseId: caseParam, count: 20 }
    : { caseId: 'all', count: 20 };

  // ── State ───────────────────────────────────────────────────────────────

  const [phase, setPhase] = useState<Phase>(initialPhase);
  const [config, setConfig] = useState<SetupConfig>(initialConfig);
  const [questions, setQuestions] = useState<QuizItem[]>(() => {
    if (drillItems) {
      const ids = drillItems.split(',').map((s) => s.trim()).filter(Boolean);
      const byId = new Map(itemPool.map((it) => [it.data.id, it]));
      return ids.map((id) => byId.get(id)).filter((it): it is QuizItem => Boolean(it));
    }
    if (deepLinkQueue && deepLinkQueue.length > 0) return deepLinkQueue;
    return [];
  });
  const [currentIdx, setCurrentIdx] = useState(0);
  const [picked, setPicked] = useState<number | null>(null);
  const [attempts, setAttempts] = useState<AttemptRecord[]>([]);
  const [sessionId, setSessionId] = useState<string>(() => mkSessionId());
  const [startedAt, setStartedAt] = useState<string>(() => new Date().toISOString());

  // ── Derived ─────────────────────────────────────────────────────────────

  const currentItem = questions[currentIdx];
  const resolvedItem = useMemo<QuizItem | undefined>(() => {
    if (!currentItem) return undefined;
    if (currentItem.kind === 'mcq') {
      return { kind: 'mcq', data: resolveItem('mcq', currentItem.data, progress.repairs) };
    }
    return currentItem;
  }, [currentItem, progress.repairs]);

  const totalQuestions = questions.length;
  const isBookmarked = resolvedItem ? Boolean(progress.bookmarks[resolvedItem.data.id]) : false;

  // ── Persistence helpers ─────────────────────────────────────────────────

  const persistActive = useMemo(
    () =>
      async (override?: { currentIdx?: number; picked?: number | null }): Promise<void> => {
        const sessionAttempts = attempts.map((a) => ({
          mcqId: a.itemId,
          pickedIndex: a.pickedIndex,
          correct: a.correct,
        }));
        const snap: ActiveQuizSession = {
          id: sessionId,
          startedAt,
          caseId: config.caseId === 'all' ? null : config.caseId,
          questionIds: questions.map((q) => q.data.id),
          currentIdx,
          picked,
          attempts: sessionAttempts,
          ...(override ? { currentIdx: override.currentIdx, picked: override.picked } : {}),
        };
        await updateProgress(draft.id, (p) => {
          p.activeQuizSession = snap;
        });
      },
    [draft.id, sessionId, startedAt, config.caseId, questions, currentIdx, picked, attempts],
  );

  async function clearActive(): Promise<void> {
    await updateProgress(draft.id, (p) => {
      p.activeQuizSession = null;
    });
    await refreshProgress();
  }

  // ── Resume / start ──────────────────────────────────────────────────────

  function resumeSession(): void {
    if (!persistedSession) return;
    const byId = new Map(itemPool.map((it) => [it.data.id, it]));
    const restored = persistedSession.questionIds
      .map((qid) => byId.get(qid))
      .filter((it): it is QuizItem => Boolean(it));
    if (restored.length === 0) return;
    setQuestions(restored);
    setCurrentIdx(persistedSession.currentIdx);
    setPicked(persistedSession.picked);
    setAttempts(persistedSession.attempts.map((a) => ({
      itemId: a.mcqId,
      pickedIndex: a.pickedIndex,
      correct: a.correct,
    })));
    setSessionId(persistedSession.id);
    setStartedAt(persistedSession.startedAt);
    setConfig({
      caseId: persistedSession.caseId ?? 'all',
      count: persistedSession.questionIds.length,
    });
    setPhase('running');
  }

  function abandonAndRestart(): void {
    void clearActive();
  }

  function startSession(): void {
    const filtered = config.caseId === 'all'
      ? itemPool
      : itemPool.filter((it) => it.data.caseId === config.caseId);
    if (filtered.length === 0) return;
    const max = config.count === 'all' ? filtered.length : Math.min(config.count, filtered.length);
    const shuffled = shuffle(filtered).slice(0, max);
    const newId = mkSessionId();
    const newStart = new Date().toISOString();
    setQuestions(shuffled);
    setCurrentIdx(0);
    setPicked(null);
    setAttempts([]);
    setSessionId(newId);
    setStartedAt(newStart);
    setPhase('running');
    void updateProgress(draft.id, (p) => {
      p.activeQuizSession = {
        id: newId,
        startedAt: newStart,
        caseId: config.caseId === 'all' ? null : config.caseId,
        questionIds: shuffled.map((q) => q.data.id),
        currentIdx: 0,
        picked: null,
        attempts: [],
      };
    });
  }

  // ── Per-step actions ────────────────────────────────────────────────────

  function pickOption(i: number): void {
    if (picked !== null || !resolvedItem) return;
    setPicked(i);
    const correct = isCorrectAnswer(resolvedItem, i);
    const nextAttempts: AttemptRecord[] = [
      ...attempts,
      { itemId: resolvedItem.data.id, pickedIndex: i, correct },
    ];
    setAttempts(nextAttempts);
    void persistActive({ picked: i });
  }

  async function nextOrFinish(): Promise<void> {
    if (currentIdx + 1 >= totalQuestions) {
      await persistSessionResults();
      setPhase('finished');
      return;
    }
    const nextIdx = currentIdx + 1;
    setCurrentIdx(nextIdx);
    setPicked(null);
    void persistActive({ currentIdx: nextIdx, picked: null });
  }

  async function handleToggleBookmark(): Promise<void> {
    if (!resolvedItem) return;
    const target = resolvedItem.data;
    await toggleBookmark(
      draft.id,
      target.id,
      resolvedItem.kind === 'tf' ? 'tf' : 'mcq',
      target.caseId ?? null,
      updateProgress,
      refreshProgress,
    );
  }

  async function persistSessionResults(): Promise<void> {
    const finishedAt = new Date().toISOString();
    const correctCount = attempts.filter((a) => a.correct).length;
    const total = attempts.length;
    const sessionCaseId = config.caseId === 'all' ? null : config.caseId;

    await updateProgress(draft.id, (p) => {
      for (const a of attempts) {
        const item = itemPool.find((it) => it.data.id === a.itemId);
        if (!item) continue;
        const entry = recordWrongAnswerEntry(
          p.wrongAnswers[a.itemId],
          a.correct,
          item.kind === 'tf' ? 'tf' : 'mcq',
          item.data.caseId ?? null,
          finishedAt,
        );
        if (entry) {
          p.wrongAnswers[a.itemId] = entry;
        }
      }
      p.quizHistory = [
        ...p.quizHistory,
        {
          id: sessionId,
          startedAt,
          finishedAt,
          caseId: sessionCaseId,
          totalQuestions: total,
          correctCount,
        },
      ].slice(-50);
      p.activeQuizSession = null;
    });
    await refreshProgress();
    eventBus.emit('quiz:completed', {
      courseId: draft.id,
      chapterId: sessionCaseId ?? '',
      score: correctCount,
      total,
    });
  }

  // ── Reset helper (called by view for "restart") ────────────────────────

  const restart = useCallback(() => {
    setPhase('setup');
    setQuestions([]);
    setAttempts([]);
    setCurrentIdx(0);
    setPicked(null);
  }, []);

  // ── Return ──────────────────────────────────────────────────────────────

  return {
    // State
    phase,
    config,
    questions,
    currentIdx,
    picked,
    attempts,
    sessionId,
    startedAt,
    totalQuestions,
    // Derived
    currentItem,
    resolvedItem,
    isBookmarked,
    persistedSession,
    // Actions
    setPhase,
    setConfig,
    startSession,
    resumeSession,
    abandonAndRestart,
    pickOption,
    nextOrFinish,
    toggleBookmark: handleToggleBookmark,
    restart,
  } as const;
}
